import React, { useEffect, useState } from 'react';
import {
  Alert, AlertIcon, Badge, Box, Button, Card, CardBody, CardHeader, Checkbox, Flex, Heading, HStack, Text, Textarea, VStack,
} from '@chakra-ui/react';
import { ChatWidgetAPI } from '../../services/api';
import ChatAttachment from '../../components/chat/ChatAttachment';
import { announceChatsChanged } from '../../hooks/useAwaitingChatCount';
import { formatCallTime, useCallTimezone } from '../../utils/callTimezone';

export const MAX_REPLY_CHARS = 2000;
const REFRESH_MS = 10 * 1000;
const ONLINE_MS = 30 * 1000;

const STATES = {
  none: ['gray', 'AI 接待中', 'AI is answering'],
  requested: ['red', '访客请求真人', 'Visitor asked for a person'],
  active: ['green', '团队成员接管中（AI 暂停）', 'Team member handling (AI paused)'],
  closed: ['gray', '已交还 AI', 'Handed back to the AI'],
};

export const handoffStateBadge = (state, lang = 'zh') => {
  const [color, zh, en] = STATES[state] || STATES.none;
  return { color, label: lang === 'zh' ? zh : en };
};

// Who wrote a message, as staff see it.
export const messageAuthor = (message, lang = 'zh') => {
  const zh = lang === 'zh';
  if (message.role === 'user') return zh ? '访客' : 'Visitor';
  if (message.role === 'staff') return `${zh ? '团队成员' : 'Team member'}${message.staff_name ? ` · ${message.staff_name}` : ''}`;
  return 'AI';
};

// The visitor's chat window polled recently (it polls every 5-15 s while open).
export const visitorOnline = (lastSeen, now = Date.now()) => {
  const at = lastSeen ? Date.parse(lastSeen) : NaN;
  return Number.isFinite(at) && now - at < ONLINE_MS;
};

const ERRORS = {
  visitor_email_missing: ['访客没有在转人工时留下邮箱，不能发邮件副本。', 'The visitor left no email when escalating, so no email copy can be sent.'],
  translation_unavailable: ['翻译暂时不可用，请稍后再试或直接发送原文。', 'Translation is unavailable; try again or send without translating.'],
  too_many_replies: ['回复太频繁了，请稍等一下。', 'Too many replies; please wait a moment.'],
  handoff_unavailable: ['真人回复功能还没有开通（数据库未升级）。', 'Staff replies are not available yet (database not upgraded).'],
  handoff_disabled: ['本网页客服未开启转人工，不能回复或接管。', 'Human handoff is off for this website chat, so you cannot reply or take over.'],
};

// Shown instead of the reply box while the merchant's widget has handoff_enabled off: the server
// refuses staff reply / take / release then (403 handoff_disabled).
export function HandoffDisabledNotice({ lang = 'zh' }) {
  const zh = lang === 'zh';
  return (
    <Alert status="info" mb={4} fontSize="sm">
      <AlertIcon />
      {zh ? '真人回复未开启：本网页客服没有开启转人工，这里只能查看对话。账号所有者可在网页客服设置里开启。'
        : 'Staff replies are off: human handoff is not enabled for this website chat, so this conversation is read-only. The account owner can turn it on in the website chat settings.'}
    </Alert>
  );
}

// Staff reply box and take-over controls for one website chat (W-H phase 1). The visitor sees
// replies within about 5 seconds while their chat window is open; the AI stays quiet while a
// team member has the conversation.
export default function ChatHandoffPanel({ detail, lang = 'zh', onChanged, showTranscript = true, composerRef }) {
  const zh = lang === 'zh';
  const [displayTimezone] = useCallTimezone();
  const [text, setText] = useState('');
  const [emailCopy, setEmailCopy] = useState(false);
  const [translate, setTranslate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const state = detail?.handoff_state || 'none';
  const polling = (state === 'requested' || state === 'active') && detail?.handoff_enabled !== false;

  useEffect(() => {
    if (!polling || !onChanged) return undefined;
    const timer = setInterval(() => { if (document.visibilityState !== 'hidden') onChanged(); }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [polling, onChanged]);

  if (!detail?.handoff_available) return null;
  if (detail.handoff_enabled === false) return <HandoffDisabledNotice lang={lang} />;
  const badge = handoffStateBadge(state, lang);
  const messages = (detail.messages || []).slice(-30);
  const describe = (e) => {
    const code = e?.response?.data?.detail?.code;
    return ERRORS[code] ? ERRORS[code][zh ? 0 : 1] : (zh ? '操作失败，请重试。' : 'Something went wrong; please try again.');
  };
  const changed = () => { announceChatsChanged(); if (onChanged) onChanged(); };

  const send = async () => {
    const content = text.trim();
    if (!content || busy) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const result = await ChatWidgetAPI.reply(detail.conversation_id, {
        content, email_copy: Boolean(emailCopy && detail.visitor_email_available), translate_to: translate ? 'en' : undefined,
      });
      setText('');
      const checks = result?.check_tokens || [];
      if (checks.length) setNotice(zh ? `译文里没有找到：${checks.join('、')}，请核对。` : `Not found in the translation: ${checks.join(', ')}. Please check.`);
      else if (result?.email_copy === 'queued') setNotice(zh ? '已发送，邮件副本正在发出。' : 'Sent; the email copy is on its way.');
      changed();
    } catch (e) {
      setError(describe(e));
    } finally {
      setBusy(false);
    }
  };

  const act = async (action) => {
    setBusy(true); setError('');
    try {
      await ChatWidgetAPI.handoff(detail.conversation_id, action);
      changed();
    } catch (e) {
      setError(describe(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card ref={composerRef} mb={0} className="chat-composer" position={showTranscript ? undefined : { base: 'static', md: 'sticky' }} bottom={0} zIndex={1}>
      <CardHeader pb={1}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
          <HStack spacing={2} wrap="wrap">
            <Heading size="md">{zh ? '回复访客' : 'Reply to the visitor'}</Heading>
            <Badge colorScheme={badge.color} textTransform="none">{badge.label}</Badge>
            {visitorOnline(detail.visitor_last_seen_at) && <Badge colorScheme="green" variant="outline" textTransform="none">{zh ? '访客在线' : 'Visitor online'}</Badge>}
          </HStack>
          <HStack spacing={2}>
            {state !== 'active' && <Button size="sm" onClick={() => act('take')} isDisabled={busy}>{zh ? '接管对话' : 'Take over'}</Button>}
            {(state === 'active' || state === 'requested') && <Button size="sm" variant="outline" onClick={() => act('release')} isDisabled={busy}>{zh ? '交还 AI' : 'Hand back to AI'}</Button>}
          </HStack>
        </Flex>
        <Text fontSize="sm" color="gray.500" mt={1}>
          {zh ? '访客的聊天窗口开着时，5 秒内能看到你的回复。你回复或接管后 AI 暂停；长时间没有人回复时，访客再发消息会自动交还 AI。'
            : 'Visitors with the chat open see your reply within 5 seconds. The AI pauses once you reply or take over; after a long silence the visitor\'s next message hands the chat back to the AI.'}
        </Text>
      </CardHeader>
      <CardBody pt={2}>
        {showTranscript && <VStack align="stretch" spacing={2} maxH="40vh" overflowY="auto" mb={3}>
          {messages.map((m) => (
            <Box key={m.turn_id} p={2} rounded="md" bg={m.role === 'user' ? 'gray.50' : m.role === 'staff' ? 'teal.50' : 'blue.50'}>
              <Text fontSize="xs" color="gray.500">
                {messageAuthor(m, lang)} · {formatCallTime(m.timestamp, displayTimezone)}
                {m.held ? (zh ? ' · 等待人工' : ' · waiting for a person') : ''}
              </Text>
              <ChatAttachment conversationId={detail.conversation_id} message={m} lang={lang} />
              {/* Plain text only: links are not clickable. */}
              <Text fontSize="sm" whiteSpace="pre-wrap">{m.content}</Text>
              {m.source_text && <Text fontSize="xs" color="gray.500">{zh ? '原文：' : 'Original: '}{m.source_text}</Text>}
              {m.email_copy && <Badge mt={1} colorScheme={m.email_copy.sent ? 'green' : 'red'} textTransform="none">
                {m.email_copy.sent ? (zh ? '邮件副本已发送' : 'Email copy sent') : (zh ? '邮件副本未发出' : 'Email copy not sent')}
              </Badge>}
            </Box>
          ))}
        </VStack>}
        <Textarea size="sm" rows={3} maxLength={MAX_REPLY_CHARS} value={text} isDisabled={busy} aria-label={zh ? '回复内容' : 'Reply'}
          placeholder={zh ? '输入给访客的回复（纯文本，最多 2000 字）' : 'Write to the visitor (plain text, up to 2000 characters)'}
          onChange={(e) => setText(e.target.value)} />
        <Flex justify="space-between" align="center" mt={2} wrap="wrap" gap={2}>
          <HStack spacing={4} wrap="wrap">
            {detail.visitor_email_available && (
              <Checkbox isChecked={emailCopy} onChange={(e) => setEmailCopy(e.target.checked)}>{zh ? '同时发邮件' : 'Also email a copy'}</Checkbox>
            )}
            <Checkbox isChecked={translate} onChange={(e) => setTranslate(e.target.checked)}>{zh ? '翻译成英文' : 'Translate to English'}</Checkbox>
          </HStack>
          <Button size="sm" colorScheme="blue" onClick={send} isLoading={busy} isDisabled={!text.trim()}>{zh ? '发送' : 'Send'}</Button>
        </Flex>
        {notice && <Alert status="info" role="status" mt={2} fontSize="sm"><AlertIcon />{notice}</Alert>}
        {error && <Alert status="error" role="alert" mt={2} fontSize="sm"><AlertIcon />{error}</Alert>}
      </CardBody>
    </Card>
  );
}
